<template>
    <div v-if="isVisibleUpdateNews" class="modal-overlay">
      <div class="modal-content">
        <h3 class="title">Actualizar Noticia</h3>
        <form @submit.prevent="submitForm">
          <div class="form-group">
            <label for="title">Titulo</label>
            <input type="text" id="title" v-model="title" required />
          </div>
  
          <div class="form-group">
            <label for="year">Año</label>
            <input
              type="number"
              id="year"
              v-model="year"
              placeholder="Año"
              min="2000"
              max="2100"
              required
            />
          </div>
  
          <!-- Editor simple como textarea -->
          <div class="form-group">
            <label for="description">Descripción</label>
            <div class="editor-container">
              <div class="editor-toolbar">
                <button type="button" @click="formatText('bold')" class="format-btn">
                  <strong>B</strong>
                </button>
                <button type="button" @click="formatText('italic')" class="format-btn">
                  <em>I</em>
                </button>
                <button type="button" @click="formatText('underline')" class="format-btn">
                  <u>U</u>
                </button>
                <button type="button" @click="insertLink" class="format-btn">
                  Link
                </button>
              </div>
              <div
                ref="editorDiv"
                class="rich-editor"
                contenteditable="true"
                @input="handleInput"
                @paste="handlePaste"
                placeholder="Escribe tu contenido aquí..."
              ></div>
            </div>
          </div>
  
          <div style="display: flex; justify-content: flex-end;">
            <button type="submit" :disabled="!title || !description || !year">Enviar</button>
            <button type="button" @click="closeModal">Cerrar</button>
          </div>
        </form>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, watch, defineComponent, nextTick } from "vue";
  import { showSuccessToast } from "@/kernel/alerts";
  import { updateNews } from "../services/news-service";
  
  export default defineComponent({
    props: {
      isVisibleUpdateNews: {
        type: Boolean,
        required: true,
      },
      news: {
        type: Object,
        required: true,
      },
    },
    emits: ["update:visible", "update:news"],
    setup(props, { emit }) {
      // Campos del formulario
      const id = ref(0);
      const title = ref("");
      const year = ref("");
      const description = ref("");
  
      // Ref para el editor
      const editorDiv = ref(null);
  
      // Manejar input del editor
      const handleInput = (event) => {
        description.value = event.target.innerHTML;
      };
  
      // Manejar paste
      const handlePaste = (event) => {
        event.preventDefault();
        const text = event.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text);
      };
  
      // Formatear texto
      const formatText = (command) => {
        document.execCommand(command, false, null);
        editorDiv.value.focus();
      };
  
      // Insertar link
      const insertLink = () => {
        const url = prompt('Ingresa la URL:');
        if (url) {
          document.execCommand('createLink', false, url);
          editorDiv.value.focus();
        }
      };
  
      // Cerrar modal
      const closeModal = () => {
        emit("update:visible", false);
        resetForm();
      };
  
      // Resetear formulario
      const resetForm = () => {
        id.value = 0;
        title.value = "";
        year.value = "";
        description.value = "";
        if (editorDiv.value) {
          editorDiv.value.innerHTML = "";
        }
      };
  
      // Cargar datos de la noticia
      const loadNewsData = (newsData) => {
        if (newsData) {
          id.value = newsData.id;
          title.value = newsData.title;
          year.value = newsData.year;
          description.value = newsData.description || "";
          
          // Cargar contenido en el editor
          if (editorDiv.value) {
            editorDiv.value.innerHTML = newsData.description || "";
          }
        }
      };
  
      // Enviar formulario
      const submitForm = async () => {
        const newsData = {
          id: id.value,
          title: title.value,
          description: description.value,
          year: year.value,
        };
  
        try {
          const result = await updateNews(newsData);
          if (result.status === 200) {
            showSuccessToast("Noticia actualizada correctamente");
            emit("update:news");
            closeModal();
          }
        } catch (error) {
          console.error("Error al enviar los datos:", error);
        }
      };
  
      // Watcher para la visibilidad del modal
      watch(
        () => props.isVisibleUpdateNews,
        async (newValue) => {
          if (newValue) {
            await nextTick();
            setTimeout(() => {
              loadNewsData(props.news);
            }, 100);
          }
        }
      );
  
      // Watcher para los datos de la noticia
      watch(
        () => props.news,
        (newValue) => {
          if (newValue && props.isVisibleUpdateNews) {
            setTimeout(() => {
              loadNewsData(newValue);
            }, 100);
          }
        },
        { deep: true }
      );
  
      return {
        id,
        title,
        year,
        description,
        editorDiv,
        handleInput,
        handlePaste,
        formatText,
        insertLink,
        closeModal,
        submitForm,
      };
    },
  });
  </script>
  
  <style scoped>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal-content {
    background: white;
    padding: 20px;
    border-radius: 8px;
    width: 500px;
    max-width: 90%;
    max-height: 90vh;
    overflow-y: auto;
  }
  
  .form-group {
    margin-bottom: 15px;
  }
  
  label {
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
  }
  
  input,
  select {
    width: 100%;
    padding: 8px;
    margin-top: 5px;
    border-radius: 4px;
    border: 1px solid #ccc;
    box-sizing: border-box;
  }
  
  button {
    padding: 10px 15px;
    margin-top: 10px;
    cursor: pointer;
    background-color: orange;
    color: white;
    border: none;
    border-radius: 16px;
  }
  
  button[type="submit"]:hover {
    background-color: #ff8c00;
  }
  
  button[type="button"] {
    margin-left: 16px;
    background-color: darkgray;
  }
  
  button[type="button"]:hover {
    background-color: gray;
  }
  
  .title {
    margin-top: 0;
    color: orange;
  }
  
  button[disabled] {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  /* Estilos para el editor personalizado */
  .editor-container {
    border: 1px solid #ccc;
    border-radius: 4px;
    background: white;
  }
  
  .editor-toolbar {
    display: flex;
    gap: 5px;
    padding: 8px;
    background: #f8f9fa;
    border-bottom: 1px solid #ccc;
  }
  
  .format-btn {
    padding: 5px 10px !important;
    margin: 0 !important;
    background: white !important;
    border: 1px solid #ccc !important;
    border-radius: 3px !important;
    color: #333 !important;
    font-size: 12px;
    min-width: 30px;
  }
  
  .format-btn:hover {
    background: #e6e6e6 !important;
  }
  
  .rich-editor {
    min-height: 200px;
    max-height: 300px;
    overflow-y: auto;
    padding: 12px 15px;
    line-height: 1.42;
    font-size: 14px;
    outline: none;
  }
  
  .rich-editor:empty:before {
    content: attr(placeholder);
    color: #aaa;
    font-style: italic;
    pointer-events: none;
  }
  
  .rich-editor:focus {
    outline: 2px solid #007bff;
    outline-offset: -2px;
  }
  
  /* Estilos para el contenido del editor */
  .rich-editor p {
    margin: 0 0 10px 0;
  }
  
  .rich-editor ul,
  .rich-editor ol {
    margin: 0 0 10px 20px;
  }
  
  .rich-editor a {
    color: #007bff;
    text-decoration: underline;
  }
  
  .rich-editor strong {
    font-weight: bold;
  }
  
  .rich-editor em {
    font-style: italic;
  }
  
  .rich-editor u {
    text-decoration: underline;
  }
  </style>